import { yupResolver } from '@hookform/resolvers/yup';
import {
  Autocomplete,
  Button,
  DialogActions,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Stack,
  styled,
  TextField,
  Typography
} from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Select from '@mui/material/Select';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { boolean, object, string } from 'yup';
import IconSuccess from '../../../../assets/icon-success.svg';
import DetailsCard from '../../../../components/DetailsCard/DetailsCard';
import DefaultModal from '../../../../components/Modals/DefaultModal';
import PageTitle from '../../../../components/PageTitle/PageTitle';
import { dashBorder } from '../../../../constants';
import { ManagerRole } from '../../../../enums/managerRole';
import { Company } from '../../../../models/company';
import { Manager } from '../../../../models/manager';
import {
  ADMINISTRATION_MANAGERS,
  ADMINISTRATION_MANAGERS_CREATE
} from '../../../../router/constants';
import { companyServices } from '../../../../services/companyServices';
import { managerService } from '../../../../services/managerService';

const BoldText = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.grey['900']
}));

const CreateManager = (): JSX.Element => {
  const { id } = useParams();
  const location = useLocation();
  const [editing, setEditing] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [modalData, setModalData] = useState<Manager | null>(null);
  const [manager, setManager] = useState<Manager>();
  const [companies, setCompanies] = useState<Company[]>([]);
  const navigate = useNavigate();

  const managerSchema = yup.object({
    firstName: string().default('').required('First Name is required'),
    lastName: string().default('').required('Last Name is required'),
    role: string().default('MANAGER').required('Role is required'),
    activeStatus: boolean().default(true).required('Status is required'),
    company: object()
      .default(null)
      .typeError('Company is required')
      .required('Company is required'),
    email: string()
      .default('')
      .email('Primary Email must be a valid email')
      .required('Primary Email is required'),
    secondaryEmail: string().default('')
  });

  type CreateManagerForm = yup.InferType<typeof managerSchema>;

  const defaultValues = managerSchema.getDefault();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<CreateManagerForm>({
    defaultValues,
    resolver: yupResolver(managerSchema)
  });

  const getManager = () => {
    managerService.getManagerById(Number(id)).then((response) => {
      setManager(response.data);
    });
  };

  useEffect(() => {
    if (id && location.pathname === `/create-manager/${id}`) {
      getManager();
    } else if (id) {
      getManager();
      setEditing(true);
    } else {
      setEditing(false);
      reset();
    }
  }, [id]);

  useEffect(() => {
    reset(manager as unknown as CreateManagerForm);
  }, [manager]);

  useEffect(() => {
    getCompanies();
  }, []);

  const onSubmit: SubmitHandler<CreateManagerForm> = (
    data: CreateManagerForm
  ) => {
    const payload = {
      ...data,
      companyId: data.company.id
    } as unknown as Manager;
    editing ? updateManager(payload) : createManager(payload);
  };

  const getCompanies = () => {
    companyServices.getCompaniesAutocomplete().then((response) => {
      setCompanies(response.data);
    });
  };

  const updateManager = (manager: Manager) => {
    managerService.updateManagerByAdmin(manager).then(() => {
      setModalData(manager);
      setSuccessModalOpen(true);
    });
  };

  const createManager = (manager: Manager) => {
    managerService.createManagerByAdmin(manager).then(() => {
      setModalData(manager);
      setSuccessModalOpen(true);
    });
  };

  const handleClose = () => {
    setSuccessModalOpen(false);
    navigate(`${ADMINISTRATION_MANAGERS_CREATE}`);
    reset({});
  };

  return (
    <>
      <PageTitle>{editing ? 'Edit Manager' : 'Add New Manager'}</PageTitle>
      <DetailsCard title={editing ? `Edit Manager` : 'New Manager'}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Stack sx={{ ...dashBorder }} gap={3}>
                <Typography variant="h6" color="grey.900" mb={2}>
                  Contact Information
                </Typography>
                <Controller
                  name="role"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <FormControl fullWidth>
                      <InputLabel id="role">Role*</InputLabel>
                      <Select
                        id="role"
                        label="Role"
                        fullWidth
                        value={value}
                        onChange={(data) => {
                          onChange(data.target.value);
                        }}
                      >
                        <ListSubheader
                          sx={{
                            borderBottom: '1px solid #DDDDDD ',
                            fontWeight: '400',
                            color: '#B3B3B3'
                          }}
                        >
                          Role
                        </ListSubheader>
                        <MenuItem value={'MANAGER'}>Manager</MenuItem>
                        <MenuItem value={'COMPANY_MANAGER'}>
                          Company Manager
                        </MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="First Name *"
                      error={!!errors.firstName}
                      helperText={
                        errors.firstName ? errors.firstName?.message : ''
                      }
                      {...field}
                    />
                  )}
                />
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Last Name *"
                      error={!!errors.lastName}
                      helperText={
                        errors.lastName ? errors.lastName?.message : ''
                      }
                      inputProps={{
                        maxLength: 50
                      }}
                      {...field}
                    />
                  )}
                />
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="email"
                      label="Primary Email Address *"
                      variant="outlined"
                      error={!!errors.email}
                      helperText={errors.email ? errors.email?.message : ''}
                    />
                  )}
                />
                <Controller
                  name="secondaryEmail"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="email"
                      label="Secondary Email Address "
                      variant="outlined"
                    />
                  )}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack sx={{ ...dashBorder }} gap={3}>
                <Typography variant="h6" color="grey.900" mb={2}>
                  Account Settings
                </Typography>
                <Controller
                  name="activeStatus"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <FormControl fullWidth>
                      <InputLabel id="role">Status*</InputLabel>
                      <Select
                        id="status"
                        label="Status"
                        fullWidth
                        value={value}
                        onChange={(data) => {
                          onChange(data.target.value);
                        }}
                      >
                        <ListSubheader
                          sx={{
                            borderBottom: '1px solid #DDDDDD ',
                            fontWeight: '400',
                            color: '#B3B3B3'
                          }}
                        >
                          Status
                        </ListSubheader>
                        <MenuItem value={'true'}>Active</MenuItem>
                        <MenuItem value={'false'}>Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
                <Controller
                  name="company"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Autocomplete
                      options={companies}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      filterSelectedOptions
                      value={value}
                      onChange={(e, data) => onChange(data)}
                      renderInput={(params) => (
                        <TextField
                          error={!!errors.company}
                          helperText={errors.company?.message}
                          {...params}
                          label="Select Company *"
                        />
                      )}
                    />
                  )}
                />
              </Stack>
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'end', mt: 3 }}>
            <Button
              onClick={() => {
                handleSubmit(onSubmit)();
              }}
              sx={{ width: 150 }}
            >
              {editing ? 'Update' : 'Save'}
            </Button>
          </Box>
        </form>
      </DetailsCard>
      <DefaultModal open={successModalOpen} handleClose={handleClose}>
        <Box sx={{ px: 6 }}>
          <Box textAlign="center" mb={3}>
            <img src={IconSuccess} alt="success" />
          </Box>
          <Typography variant="h3" textAlign="center" color="grey.900">
            Success!
          </Typography>
          <Typography variant="h5" textAlign="center" color="grey.900">
            {editing
              ? 'The manager has been updated'
              : 'A new manager has been created'}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              my: 3
            }}
            gap={3}
          >
            <Stack spacing={1} sx={{ flexShrink: 0 }}>
              <BoldText>First Name:</BoldText>
              <BoldText>Last Name:</BoldText>
              <BoldText>Role:</BoldText>
              <BoldText>Primary Email Address:</BoldText>
              <BoldText>Secondary Email Address:</BoldText>
              <BoldText>Status:</BoldText>
            </Stack>
            <Stack spacing={1}>
              <Typography>{modalData?.firstName}</Typography>
              <Typography>{modalData?.lastName}</Typography>
              <Typography>
                {modalData?.role && ManagerRole[modalData?.role]}
              </Typography>
              <Typography>{modalData?.email}</Typography>
              <Typography>
                {modalData?.secondaryEmail ? modalData?.secondaryEmail : 'None'}
              </Typography>
              <Typography>
                {modalData?.activeStatus ? 'Active' : 'Inactive'}
              </Typography>
            </Stack>
          </Box>
        </Box>

        <DialogActions sx={{ justifyContent: 'center', p: 3 }}>
          <Button
            variant="outlined"
            onClick={() => navigate(`${ADMINISTRATION_MANAGERS}`)}
            sx={{ mr: 4 }}
          >
            View All Managers
          </Button>
          <Button onClick={handleClose}>Create Another Manager</Button>
        </DialogActions>
      </DefaultModal>
    </>
  );
};
export default CreateManager;
